import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DocumentsService } from '@lib/services/documents.service';
import { getRawFileId } from '@lib/utils/app-utils';
import { GraphQLErrors, QueryManagerService } from 'finzzle-angular-utils';
import { isEqual } from 'lodash';
import { BehaviorSubject, distinctUntilChanged, filter, first, firstValueFrom, lastValueFrom, map } from 'rxjs';
import {
  DerStateTransitionTrigger,
  DetailedInvestisseurFragment,
  EtatDer,
  EtatQpi,
  EtatRecueil,
  FileMetadata,
  GetInvestisseurCodeByIdGQL,
  GetRecueilStateGQL,
  Investisseur,
  InvestisseurByCodeGQL,
  InvestisseurFilterInput,
  InvestisseurRelationByCodeGQL,
  InvestisseurRelationByCodeQuery,
  InvestisseursGetInvestisseursByNamePaginatedGQL,
  InvestisseurSortInput,
  ModeleDocument,
  ModelesDocumentsGQL,
  QpiStateTransitionTrigger,
  RecueilStateTransitionTrigger,
  SignatureState,
  UpdateDateModificationFicheGQL,
  UpdateDerStateGQL,
  UpdatePieceIdentiteStateGQL,
  UpdateQpiStateGQL,
  UpdateRecueilStateGQL,
  UpdateSituationPersonnelleStateGQL,
  UpdateSituationPersonnelleWithConjointStateGQL,
} from '../../../../minotaure-lib/src/public-api';
import { AuthService } from './auth-service.service';

@Injectable({
  providedIn: 'root',
})
export class InvestisseurService {
  private documentService = inject(DocumentsService);
  private queryManagerService = inject(QueryManagerService);
  private authService = inject(AuthService);
  private getInvestisseursByNameGQL = inject(InvestisseursGetInvestisseursByNamePaginatedGQL);
  private getInvestisseurByCodeGQL = inject(InvestisseurByCodeGQL);
  private getRecueilStateGQL = inject(GetRecueilStateGQL);
  private updateSituationPersonnelleStateGQL = inject(UpdateSituationPersonnelleStateGQL);
  private updateSituationPersonnelleStateWithConjointGQL = inject(UpdateSituationPersonnelleWithConjointStateGQL);
  private updatePieceIdentiteGQL = inject(UpdatePieceIdentiteStateGQL);
  private updateDERstateGQL = inject(UpdateDerStateGQL);
  private updateRecueilStateGQL = inject(UpdateRecueilStateGQL);
  private updateQPIStateGQL = inject(UpdateQpiStateGQL);
  private updateDateModificationFicheGQL = inject(UpdateDateModificationFicheGQL);
  private getInvestisseurCodeByIdGQL = inject(GetInvestisseurCodeByIdGQL);
  private readonly getModelesDocumentsGQL = inject(ModelesDocumentsGQL);
  private readonly investisseurRelationByCodeGQL = inject(InvestisseurRelationByCodeGQL);
  public consultantCode = signal<string | null>(null);
  public investisseurSubject = new BehaviorSubject<DetailedInvestisseurFragment | null>(null);
  public investisseur$ = this.investisseurSubject.asObservable();
  public investisseurSig = toSignal(
    this.investisseur$.pipe(
      distinctUntilChanged(),
      filter((inv): inv is Investisseur => inv !== null)
    ),
    { initialValue: null }
  );
  readonly isClient = computed<boolean>(() => this.investisseurSig()?.statutCode === 'CLIENT');
  public investisseurConjointSig = toSignal(
    this.investisseur$.pipe(
      distinctUntilChanged(),
      filter((inv): inv is Investisseur => inv !== null),
      map(inv => {
        if (inv.personnePhysique?.[0]?.relationCouplePersonnePhysique) {
          const autrePP = inv.personnePhysique[0].relationCouplePersonnePhysique.autrePersonnePhysique;
          if (autrePP) {
            const conjointInvestisseur: Investisseur = {
              id: autrePP.investisseur?.[0]?.id,
              code: autrePP.investisseur?.[0]?.code,
              statutCode: autrePP.investisseur?.[0]?.statutCode,
              personnePhysique: [autrePP],
              avancementConnaissanceClientInvestisseur: autrePP.investisseur?.[0]?.avancementConnaissanceClientInvestisseur,
            } as Investisseur;
            return conjointInvestisseur;
          }
        }
        return null;
      })
    ),
    { initialValue: null }
  );
  public investisseurSelectedEtiquettesSubject = new BehaviorSubject<number[]>([]);
  public investisseurSelectedEtiquettes$ = this.investisseurSelectedEtiquettesSubject.asObservable();
  public investisseurName$ = this.investisseur$.pipe(
    filter((inv): inv is Investisseur => inv !== null && !!inv.personnePhysique?.length),
    map(inv => `${inv.personnePhysique[0].prenom.trim()} ${inv.personnePhysique[0].nom.trim()}`)
  );
  public investisseurNameSig = toSignal(this.investisseurName$, { initialValue: null });
  public investisseurConjointNameSig = computed(() => {
    const conjoint = this.investisseurConjointSig();
    if (!conjoint || !conjoint.personnePhysique?.length) return null;
    return `${conjoint.personnePhysique[0].prenom.trim()} ${conjoint.personnePhysique[0].nom.trim()}`;
  });
  public isInvestisseurDead = computed(() => {
    const investisseur = this.investisseurSig();
    if (!investisseur) return false;
    const etatCivil = investisseur.personnePhysique?.[0]?.etatCivil;
    return etatCivil?.typeSituationEtatCivilCode === 'DECEDE';
  });
  readonly investisseurEmail = computed<string | undefined>(() => {
    const investisseur = this.investisseurSig();
    const personne = investisseur?.personnePhysique[0];
    const email = personne?.email?.find(t => t.estAdresseCorrespondance !== false) ?? personne?.email?.[0];
    return email?.adresse;
  });
  public investisseurDocumentsSubject = new BehaviorSubject<FileMetadata[]>([]);
  public investisseurDocuments$ = this.investisseurDocumentsSubject.asObservable();
  public coupleInvestisseurIds = computed(() => [this.investisseurSig(), this.investisseurConjointSig()].filter(Boolean).map(i => i!.id));
  public async getInvestisseur(code: string): Promise<void> {
    if (!code || code === 'null' || code === 'undefined') return;
    const result = await this.queryManagerService.handleQueryErrorsAsync(
      this.getInvestisseurByCodeGQL.fetch({
        fetchPolicy: 'network-only',
        variables: { code },
      })
    );
    const investor = (result.data?.investisseurByCode as DetailedInvestisseurFragment | undefined) ?? null;
    this.investisseurSubject.next(investor);
    this.investisseurSelectedEtiquettesSubject.next(result.data!.investisseurByCode?.etiquettes?.map(e => e.id) ?? []);
  }

  public async fetchInvestisseurByCode(code: string): Promise<DetailedInvestisseurFragment | null> {
    if (!code || code === 'null' || code === 'undefined') return null;
    const result = await this.queryManagerService.handleQueryErrorsAsync(
      this.getInvestisseurByCodeGQL.fetch({
        fetchPolicy: 'network-only',
        variables: { code },
      })
    );
    return (result.data?.investisseurByCode as DetailedInvestisseurFragment | undefined) ?? null;
  }
  public async getInvestisseursByNamePaginated(
    order: InvestisseurSortInput,
    pageIndex: number,
    pageSize: number,
    where: InvestisseurFilterInput,
    searchText: string
  ) {
    const queryResult = await this.queryManagerService.handleQueryErrorsAsync(
      this.getInvestisseursByNameGQL.fetch({
        fetchPolicy: 'network-only',
        variables: {
          order: order,
          take: pageSize,
          skip: pageIndex,
          where: where,
          searchText: searchText,
        },
      })
    );
    return queryResult.data!.investisseursByNamePaginated;
  }
  public async refreshRecueilState() {
    if (!this.investisseurSubject?.value?.code) return;
    const queryResult = await this.queryManagerService.handleQueryErrorsAsync(
      this.getRecueilStateGQL.fetch({
        fetchPolicy: 'network-only',
        variables: {
          code: this.investisseurSubject.value.code,
        },
      })
    );
    const current = this.investisseurSubject.value;
    // On ne fusionne que le champ réellement récupéré par cette requête (avancementConnaissanceClientInvestisseur) :
    // un spread générique du résultat risque d'écraser/omettre d'autres champs (ex: personnePhysique) selon la
    // forme exacte renvoyée, ce qui plante les consommateurs de investisseur$ qui les lisent sans garde.
    const avancement = queryResult.data?.investisseurByCode?.avancementConnaissanceClientInvestisseur;
    if (current && avancement && !isEqual(avancement, current.avancementConnaissanceClientInvestisseur)) {
      this.investisseurSubject.next({ ...current, avancementConnaissanceClientInvestisseur: avancement });
    }
  }
  public async updateInvestisseurSituationPersonnelleStatus(
    investisseurId: number,
    isSituationPersonnelleFormCompleted: boolean,
    conjointId?: number
  ): Promise<void> {
    let result;
    if (conjointId) {
      result = await this.queryManagerService.handleMutationErrorsAsync(
        this.updateSituationPersonnelleStateWithConjointGQL.mutate({
          fetchPolicy: 'network-only',
          variables: {
            investisseurId: investisseurId,
            conjointId: conjointId,
            isCompleted: isSituationPersonnelleFormCompleted,
          },
        })
      );
    } else {
      result = await this.queryManagerService.handleMutationErrorsAsync(
        this.updateSituationPersonnelleStateGQL.mutate({
          fetchPolicy: 'network-only',
          variables: {
            investisseurId: investisseurId,
            isCompleted: isSituationPersonnelleFormCompleted,
          },
        })
      );
    }
    // directly update the metadatas from returned value
    if (this.investisseurSubject.value && result?.data?.investisseur) {
      const investisseurAvancement = result.data!.investisseur.avancementsConnaissanceClientInvestisseurs?.find(
        av => av.investisseurId === this.investisseurSubject.value?.id
      );
      if (investisseurAvancement) {
        this.investisseurSubject.next({
          ...this.investisseurSubject.value,
          ...{ metadatasConnaissanceClient: { ...result.data!.investisseur } },
        });
      }
    }
    this.refreshRecueilState();
  }
  public async updateInvestisseurPieceIdentiteStatus(isPieceIdentiteProvided: boolean, documentId?: string): Promise<void> {
    const result = await this.queryManagerService.handleMutationErrorsAsync(
      this.updatePieceIdentiteGQL.mutate({
        fetchPolicy: 'network-only',
        variables: {
          investisseurId: this.investisseurSubject.value?.id,
          isCompleted: isPieceIdentiteProvided,
          documentId: documentId,
        },
      })
    );
    // directly update the metadatas from returned value
    if (this.investisseurSubject.value && result?.data?.updateInvestisseurPieceIdentiteState) {
      this.investisseurSubject.next({
        ...this.investisseurSubject.value,
        ...{ metadatasConnaissanceClient: { ...result.data!.updateInvestisseurPieceIdentiteState } },
      });
    }
  }
  public async updateConjointPieceIdentiteStatus(isPieceIdentiteProvided: boolean, documentId?: string): Promise<void> {
    const conjointId = this.investisseurConjointSig()?.id;
    if (!conjointId) {
      console.error('No conjoint investisseur found to update piece identite status.');
      return;
    }
    await this.queryManagerService.handleMutationErrorsAsync(
      this.updatePieceIdentiteGQL.mutate({
        fetchPolicy: 'network-only',
        variables: {
          investisseurId: this.investisseurConjointSig()?.id,
          isCompleted: isPieceIdentiteProvided,
          documentId: documentId,
        },
      })
    );
  }
  public async updateDERState(
    trigger: DerStateTransitionTrigger,
    applyAlsoToConjoint: boolean,
    documentId?: string,
    investisseurId?: number,
    dateReference?: Date
  ): Promise<void> {
    const referenceDate = dateReference ? dateReference.toISOString() : null;
    const variables = {
      investisseurId: investisseurId ?? this.investisseurSubject.value?.id,
      trigger: trigger,
      applyAlsoToConjoint: applyAlsoToConjoint,
      documentId: documentId,
      referenceDate: referenceDate,
    };
    const result = await lastValueFrom(
      this.updateDERstateGQL.mutate({
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
        variables: variables,
      })
    );
    const updatedDerState = result?.data?.updateInvestisseurDERState;
    if (!updatedDerState) {
      throw result?.error ?? new Error('Failed to update DER state');
    }
    // directly update the metadatas from returned value
    if (this.investisseurSubject.value && updatedDerState) {
      this.investisseurSubject.next({
        ...this.investisseurSubject.value,
        ...{ metadatasConnaissanceClient: { ...updatedDerState } },
      });
    }
  }
  private handleDERStatusUpdate(investisseur: Investisseur, files: FileMetadata[]): void {
    const derDocuments = files.filter(
      f =>
        f.typeDocumentCode === 'DER' &&
        getRawFileId(f.fileId) === getRawFileId(investisseur.avancementConnaissanceClientInvestisseur?.deRdocumentID)
    );
    if (derDocuments?.length > 0) {
      const latest = derDocuments.reduce((a, b) =>
        new Date(a.dateDeDerniereModification) > new Date(b.dateDeDerniereModification) ? a : b
      );
      const documentInvestisseurIds = latest.investisseurIds ?? [];
      const coupleInvestisseurIds = this.coupleInvestisseurIds();
      const applyToConjoint = coupleInvestisseurIds.length > 1 && coupleInvestisseurIds.every(id => documentInvestisseurIds.includes(id));
      if (
        latest.etatSignature === SignatureState.Completed &&
        investisseur.avancementConnaissanceClientInvestisseur?.derEtat !== EtatDer.Signe
      ) {
        this.updateDERState(DerStateTransitionTrigger.DerHasBeenSigned, applyToConjoint, getRawFileId(latest.fileId!));
      }
      if (
        latest.etatSignature === SignatureState.Failed &&
        investisseur.avancementConnaissanceClientInvestisseur?.derEtat === EtatDer.EnSignature
      ) {
        this.updateDERState(DerStateTransitionTrigger.DerSignatureIsCancelled, applyToConjoint, getRawFileId(latest.fileId!));
      }
    }
  }
  public async updateRecueilState(
    trigger: RecueilStateTransitionTrigger,
    documentId?: string,
    investisseurId?: number,
    fileInvestisseurIds?: number[],
    dateReference?: Date
  ): Promise<void> {
    const referenceDate = dateReference ? dateReference.toISOString() : null;
    const variables = {
      investisseurId: investisseurId ?? this.investisseurSubject.value?.id,
      trigger: trigger,
      applyAlsoToConjoint: (fileInvestisseurIds ?? 0 > 1) ? true : false,
      documentId: documentId,
      referenceDate: referenceDate,
    };
    const result = await lastValueFrom(
      this.updateRecueilStateGQL.mutate({
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
        variables: variables,
      })
    );
    const updatedRecueilState = result?.data?.updateInvestisseurRecueilState;
    if (!updatedRecueilState) {
      throw result?.error ?? new Error('Failed to update recueil state');
    }
    // directly update the metadatas from returned value
    if (this.investisseurSubject.value && updatedRecueilState) {
      this.investisseurSubject.next({
        ...this.investisseurSubject.value,
        ...{ metadatasConnaissanceClient: { ...updatedRecueilState } },
      });
    }
  }
  public async updateQPIState(
    trigger: QpiStateTransitionTrigger,
    documentId?: string,
    investisseurId?: number,
    dateReference?: Date
  ): Promise<void> {
    const referenceDate = dateReference ? dateReference.toISOString() : null;
    const variables = {
      investisseurId: investisseurId ?? this.investisseurSubject.value?.id,
      trigger: trigger,
      documentId: documentId,
      referenceDate: referenceDate,
    };
    const result = await firstValueFrom(
      this.updateQPIStateGQL.mutate({
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
        variables: variables,
      })
    );
    const updatedQpiState = result?.data?.updateInvestisseurQPIState;
    if (!updatedQpiState) {
      throw result?.error ?? new Error('Failed to update QPI state');
    }
    // directly update the metadatas from returned value
    if (this.investisseurSubject.value && updatedQpiState) {
      this.investisseurSubject.next({
        ...this.investisseurSubject.value,
        ...{ metadatasConnaissanceClient: { ...updatedQpiState } },
      });
    }
  }
  private handleRecueilStatusUpdate(investisseur: Investisseur, files: FileMetadata[]): void {
    const recueilDocuments = files.filter(
      f =>
        f.typeDocumentCode === 'REC_INFO' &&
        getRawFileId(f.fileId) === getRawFileId(investisseur.avancementConnaissanceClientInvestisseur?.recueildocumentID)
    );
    if (recueilDocuments?.length > 0) {
      const latest = recueilDocuments.reduce((a, b) =>
        new Date(a.dateDeDerniereModification) > new Date(b.dateDeDerniereModification) ? a : b
      );
      if (
        latest.etatSignature === SignatureState.Completed &&
        investisseur.avancementConnaissanceClientInvestisseur?.recueilEtat !== EtatRecueil.Signe
      ) {
        this.updateRecueilState(RecueilStateTransitionTrigger.RecueilHasBeenSigned, getRawFileId(latest.fileId!));
      }
      if (
        latest.etatSignature === SignatureState.Failed &&
        [EtatRecueil.EnSignatureApresExpiration, EtatRecueil.EnSignatureApresSigne, EtatRecueil.EnSignaturePremierFois].includes(
          investisseur.avancementConnaissanceClientInvestisseur?.recueilEtat as EtatRecueil
        )
      ) {
        this.updateRecueilState(RecueilStateTransitionTrigger.RecueilSignatureIsCancelled, getRawFileId(latest.fileId!));
      }
    }
  }
  private handleQPIStatusUpdate(investisseur: Investisseur, files: FileMetadata[]): void {
    const qpiDocuments = files.filter(
      f =>
        f.typeDocumentCode === 'PROF_INVEST' &&
        getRawFileId(f.fileId) === getRawFileId(investisseur.avancementConnaissanceClientInvestisseur?.qpIdocumentID)
    );
    if (qpiDocuments?.length > 0) {
      const latest = qpiDocuments.reduce((a, b) =>
        new Date(a.dateDeDerniereModification) > new Date(b.dateDeDerniereModification) ? a : b
      );
      if (
        latest.etatSignature === SignatureState.Completed &&
        ![EtatQpi.Signe, EtatQpi.SigneNouveauACompleter, EtatQpi.SigneNouveauASigner].includes(
          investisseur.avancementConnaissanceClientInvestisseur?.qpiEtat as EtatQpi
        )
      ) {
        this.updateQPIState(QpiStateTransitionTrigger.QpiHasBeenSigned, getRawFileId(latest.fileId!), investisseur.id);
      }
      if (
        latest.etatSignature === SignatureState.Failed &&
        [EtatQpi.EnSignatureApresExpiration, EtatQpi.EnSignatureApresSigne, EtatQpi.EnSignaturePremierFois].includes(
          investisseur.avancementConnaissanceClientInvestisseur?.qpiEtat as EtatQpi
        )
      ) {
        this.updateQPIState(QpiStateTransitionTrigger.QpiSignatureIsCancelled, getRawFileId(latest.fileId!), investisseur.id);
      }
    }
  }
  public async getModelesDocuments(): Promise<ModeleDocument[]> {
    let investisseurCouple = false;
    if (this.investisseurConjointSig()) {
      investisseurCouple = true;
    }
    const result = await this.queryManagerService.handleQueryErrorsAsync(
      this.getModelesDocumentsGQL.fetch({
        variables: { investisseurCouple: investisseurCouple, codeConsultant: this.consultantCode() },
      })
    );
    return (result?.data?.modelesDocuments as ModeleDocument[]) || [];
  }
  public async updateDateModificationFiche(): Promise<boolean> {
    const result = await this.queryManagerService.handleMutationErrorsAsync(
      this.updateDateModificationFicheGQL.mutate({
        variables: { investisseurId: this.investisseurSubject.value?.id },
      })
    );
    return result?.data?.updateDateModificationFiche ?? false;
  }
  public async getInvestisseurDocuments(includeCancelled = false): Promise<FileMetadata[]> {
    const investisseur =
      this.investisseurSig() ??
      (await firstValueFrom(
        this.investisseur$.pipe(
          filter((v): v is Investisseur => v !== null),
          first()
        )
      ));
    const result = (await this.documentService.getInvestisseurDocumentsByInvestisseurId(investisseur?.id, includeCancelled)) ?? [];
    if (!this.authService.isBackOffice()) {
      this.handleDERStatusUpdate(investisseur as Investisseur, result);
      this.handleRecueilStatusUpdate(investisseur as Investisseur, result);
      this.handleQPIStatusUpdate(investisseur as Investisseur, result);
    }
    this.investisseurDocumentsSubject.next(result);
    return result;
  }
  public async getInvestisseursRelations(): Promise<InvestisseurRelationByCodeQuery | undefined> {
    const investisseur = this.investisseurSig();
    const coInvestisseur = this.investisseurConjointSig();
    if (!investisseur) return;
    const { data } = await this.queryManagerService.handleQueryErrorsAsync(
      this.investisseurRelationByCodeGQL.fetch({
        variables: {
          investisseurCode: investisseur.code,
          hasCoInvestisseur: !!coInvestisseur,
          coInvestisseurCode: coInvestisseur?.code,
        },
      })
    );
    return data;
  }
  public async getInvestisseurCodeById(investisseurId: number): Promise<string | undefined> {
    const result = await this.queryManagerService.handleQueryErrorsAsync(
      this.getInvestisseurCodeByIdGQL.fetch({
        fetchPolicy: 'network-only',
        variables: {
          id: investisseurId,
        },
      })
    );
    return result.data!.investisseurById?.code;
  }

  // Méthode pour gérer les erreurs liées à l'accès à l'investisseur personnephysique selon le path fourni
  // Si l'erreur ne correspond pas au path, on renvoie l'erreur pour qu'elle soit gérée par la lib
  public manageErrorInvestisseurOutOfWallet(errors: readonly any[] | undefined, mainPath: string): boolean {
    if (errors === undefined || errors.length === 0) return false;
    const isPersonnePhysiqueError = errors?.some((error: any) => {
      const path = error?.path;
      if (Array.isArray(path)) {
        return path.includes('personnePhysique') && path.includes(mainPath);
      }
      if (typeof path === 'string') {
        return path.includes('personnePhysique') && path.includes(mainPath);
      }
      return false;
    });
    if (!isPersonnePhysiqueError) {
      this.queryManagerService.snackBarGraphQlErrors(errors as GraphQLErrors);
      return false;
    }
    return true;
  }
}
